'use client';
import axios from 'axios';
import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Stepper, Step, StepLabel, Box, Button, Fade, styled, StepConnector } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockIcon from '@mui/icons-material/Lock';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';

import BasicInfo from '../../components/Estimator/BasicInfo';
import Quantities from '../../components/Estimator/Quantities';
import RowCalculator from '../../components/Estimator/RowCalculator';
import PMInputs from '../../components/Estimator/PMInputs';
import Summary from '../../components/Estimator/Summary';
import EditPricing from './EditPricing';

import { useEstimator } from '../../context/EstimatorContext';

const steps = ['Basic Info', 'Quantities', 'Row Calculator', 'PM Inputs', 'Summary'];

const CustomStepIcon = ({ active, completed }) => {
  if (completed) return <CheckCircleIcon color="success" sx={{ fontSize: 30 }} />;
  if (active) return <LockIcon color="primary" sx={{ fontSize: 30 }} />;
  return <MoreHorizIcon color="disabled" sx={{ fontSize: 30 }} />;
};

const CustomConnector = styled(StepConnector)(({ theme }) => ({
  '& .MuiStepConnector-line': {
    borderTopWidth: 3,
    transition: 'all 0.3s ease-in-out',
    borderColor: theme.palette.grey[300],
  },
  '&.Mui-active .MuiStepConnector-line': {
    borderColor: theme.palette.success.main,
  },
  '&.Mui-completed .MuiStepConnector-line': {
    borderColor: theme.palette.success.main,
  },
}));

export default function NewEstimate() {
  const [activeStep, setActiveStep] = useState(0);
  const [completed, setCompleted] = useState({});
  const [showSettings, setShowSettings] = useState(false);

  const { setFormData, formData, clearFormData } = useEstimator();
  const rowCalcRef = useRef();

  const { estimateId } = useParams();
  const location = useLocation();
  const isLoading = estimateId && !formData.estimateId;

  console.log('CONTEXT: ', formData);

  useEffect(() => {
    const loadEstimate = async () => {
      if (!estimateId && location.pathname === '/new-estimate') {
        clearFormData();
        return;
      }

      if (estimateId) {
        try {
          const res = await axios.get(`/api/PipelinePricing/pipeline/estimate-by-id/${estimateId}`);
          const data = res.data;

          setFormData({
            estimateId: data.id,
            userId: data.userId,
            estimateDate: data.estimateDate,
            estimatedBy: data.estimatedBy,
            requestedBy: data.requestedBy,
            projectName: data.projectName,
            projectScope: data.projectScope,
            lastModifiedBy: data.lastModifiedBy,
            lastUpdated: data.lastUpdated,
            quantities: (data.quantities || [])
              .sort((a, b) => (a.rowNumber || 0) - (b.rowNumber || 0))
              .map((q, index) => ({ ...q, rowNumber: index + 1 })),
            rowCalculator: data.rowCalculations || [],
            overrideCost: data.rowSummary?.overrideCost ?? '',
            averageROWCostPerFoot: data.rowSummary?.averageROWCostPerFoot ?? null,
            averageROWCostPerRod: data.rowSummary?.averageROWCostPerRod ?? null,
            totalPercentOfRoute: data.rowSummary?.totalPercentOfRoute ?? null,
            pipeSpecs: (data.pipeSpecs ?? []).map((spec) => ({
              ...spec,
              internalCoating: spec.internalCoating === true || spec.internalCoating === 'Yes' ? 'Yes' : 'No',
            })),
            costAssumptions: data.costAssumptions ?? [],
            specialItems: data.specialItems ?? [],
            costCategory: [],
          });
        } catch (err) {
          console.error('Failed to load estimate', err);
          alert('Could not load estimate');
        }
      }
    };

    loadEstimate();

    return () => {
      const stillInEstimator =
        location.pathname.startsWith('/new-estimate') || location.pathname.startsWith('/estimates/continue');

      if (!stillInEstimator) {
        clearFormData();
      }
    };
  }, [estimateId]);

  const handleNext = () => {
    setCompleted((prev) => ({ ...prev, [activeStep]: true }));
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const isStepComplete = (step) => !!completed[step];

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return <BasicInfo onComplete={handleNext} />;
      case 1:
        return <Quantities onComplete={handleNext} />;
      case 2:
        return <RowCalculator ref={rowCalcRef} onComplete={handleNext} />;
      case 3:
        return <PMInputs onComplete={handleNext} />;
      case 4:
        return <Summary />;
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ width: '100%', p: 4, textAlign: 'center' }}>
        <p>Loading estimate...</p>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', p: 4 }}>
      {showSettings ? (
        <>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-start' }}>
            <Button variant="contained" onClick={() => setShowSettings(false)}>
              Back
            </Button>
          </Box>
          <EditPricing />
        </>
      ) : (
        <>
          <Stepper activeStep={activeStep} alternativeLabel connector={<CustomConnector />}>
            {steps.map((label, index) => (
              <Step key={label} completed={isStepComplete(index)}>
                <StepLabel
                  StepIconComponent={(props) => <CustomStepIcon active={props.active} completed={props.completed} />}
                  onClick={() => setActiveStep(index)}
                  sx={{ cursor: 'pointer' }}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          <Fade in timeout={500}>
            <Box sx={{ mt: 4 }}>{renderStepContent()}</Box>
          </Fade>

          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-start' }}>
            {activeStep > 0 && (
              <Button onClick={handleBack} variant="outlined">
                Back
              </Button>
            )}
          </Box>
        </>
      )}
    </Box>
  );
}
